import { measureText, Rect, TextOption } from '@syncfusion/ej2-svg-base';
import { extend, getValue } from '@syncfusion/ej2-base';
import { getVisiblePoints, ChartLocation, convertHexToColor, colorNameToHex, textElement, isCollide, RectOption, appendChildElement } from '../../common/utils/helper';
/**
 * The 'SeriesLabel' module is used to render the series name near the series.
 */
export class SeriesLabel {
    /**
     * Constructor for the series label module.
     *
     * @param {Chart} chart - The parent chart.
     */
    constructor(chart) {
        this.chart = chart;
    }
    /**
     * Render the series label for series.
     *
     * @param {Series} series - The series to render.
     * @param {Chart} chart - The parent chart.
     * @param {SeriesLabelSettingsModel} seriesLabel - The settings for series labels.
     * @returns {void}
     * @private
     */
    render(series, chart, seriesLabel) {
        if (!series.visible || !seriesLabel.visible) {
            return;
        }
        const group = chart.seriesLabelElements;
        const visiblePoints = this.getZoomVisiblePoints(series);
        if (visiblePoints.length < 1) {
            return;
        }
        this.renderSeriesLabel(series, visiblePoints, group, seriesLabel);
    }
    /**
     * Render series label per series in proper anchor position.
     *
     * @param {Series} series - The current series object.
     * @param {Points[]} visiblePoints - the array of visible points in the series.
     * @param {HTMLElement} element - element that contains the series label elements
     * @param {SeriesLabelSettingsModel} seriesLabel - series label setting model.
     * @returns {Element[]} - Returns the created series label elements (empty if not placed).
     * @private
     */
    renderSeriesLabel(series, visiblePoints, element, seriesLabel) {
        const labelElements = [];
        const labelText = series.labelSettings.text ? series.labelSettings.text : series.name;
        const font = extend({}, getValue('properties', seriesLabel.font), null, true);
        const textSize = measureText(labelText, font, this.chart.themeStyle.seriesLabelFont);
        const seriesRect = [];
        const clip = series.clipRect;
        const offset = 5;
        const errorBarRects = this.buildErrorBarRects(series);
        const blockers = [...this.chart.seriesLabelCollections, ...this.chart.dataLabelCollections,
            ...this.chart.axisLabelCollections, ...this.chart.markerCollections, ...errorBarRects];
        const allowOverlap = seriesLabel.showOverlapText;
        const validPoints = visiblePoints.filter((point) => {
            return !!(point && point.symbolLocations && point.symbolLocations[0]);
        });
        const finalOpacity = series.labelSettings.opacity ? series.labelSettings.opacity : series.labelSettings.font.opacity;
        const animationDelay = series.animation.enable ? (series.animation.duration + series.animation.delay) : 0;
        const isInitialRender = !this.chart.redraw && series.animation.enable;
        // Early return if no valid points
        if (validPoints.length === 0 || !clip) {
            return labelElements;
        }
        // Get the last valid point (or first if only one)
        const lastPoint = validPoints[validPoints.length - 1];
        const lastSymbolLocation = lastPoint.symbolLocations[0];
        // Find peak point (point with minimum Y value)
        const peakPoint = validPoints.reduce((prev, curr) => {
            const prevY = prev.symbolLocations[0].y;
            const currY = curr.symbolLocations[0].y;
            return currY < prevY ? curr : prev;
        }, validPoints[0]);
        const anchorCandidates = [
            this.addClipRectToPosition(lastSymbolLocation, clip),
            this.addClipRectToPosition(peakPoint.symbolLocations[0], clip)
        ];
        const padding = 4;
        const background = seriesLabel.background;
        const border = seriesLabel.border;
        const useShape = (background && background !== 'transparent') || (border && border.width && border.width > 0);
        const effectiveWidth = textSize.width + (useShape ? padding * 2 : 0);
        for (let j = 0; j < this.chart.visibleSeries.length; j++) {
            const currentSeries = this.chart.visibleSeries[j];
            const seriesPoints = currentSeries.points;
            if (!currentSeries || !currentSeries.visible || !seriesPoints || !Array.isArray(seriesPoints)) {
                continue;
            }
            const lineWidth = currentSeries.border.width || 2;
            for (let pointIndex = seriesPoints.length - 1; pointIndex > 0; pointIndex--) {
                const currPoint = seriesPoints[pointIndex];
                const prevPoint = seriesPoints[pointIndex - 1];
                if (currPoint && prevPoint &&
                    currPoint.symbolLocations && prevPoint.symbolLocations &&
                    currPoint.symbolLocations[0] && prevPoint.symbolLocations[0]) {
                    const currentPosition = this.addClipRectToPosition(currPoint.symbolLocations[0], currentSeries.clipRect);
                    const previousPosition = this.addClipRectToPosition(prevPoint.symbolLocations[0], currentSeries.clipRect);
                    const segmentRect = this.createRectFromPoints(currentPosition, previousPosition);
                    const halfWidth = lineWidth / 2;
                    segmentRect.y -= halfWidth;
                    segmentRect.height += lineWidth;
                    seriesRect.push(segmentRect);
                    if (Math.abs(currentPosition.x - previousPosition.x) < effectiveWidth) {
                        if (pointIndex - 2 >= 0 && seriesPoints[pointIndex - 2] &&
                            seriesPoints[pointIndex - 2].symbolLocations &&
                            seriesPoints[pointIndex - 2].symbolLocations[0]) {
                            const adjRect = this.createRectFromPoints(previousPosition, this.addClipRectToPosition(seriesPoints[pointIndex - 2].symbolLocations[0], clip));
                            adjRect.y -= halfWidth;
                            adjRect.height += lineWidth;
                            seriesRect.push(adjRect);
                        }
                        if (pointIndex + 1 < seriesPoints.length && seriesPoints[pointIndex + 1] &&
                            seriesPoints[pointIndex + 1].symbolLocations && seriesPoints[pointIndex + 1].symbolLocations[0]) {
                            const adjRect = this.createRectFromPoints(currentPosition, this.addClipRectToPosition(seriesPoints[pointIndex + 1].symbolLocations[0], clip));
                            adjRect.y -= halfWidth;
                            adjRect.height += lineWidth;
                            seriesRect.push(adjRect);
                        }
                    }
                }
            }
        }
        if (validPoints.length > 1) {
            for (let i = validPoints.length - 1; i > 0; i--) {
                const currentPoint = validPoints[i];
                const previousPoint = validPoints[i - 1];
                if (currentPoint && previousPoint &&
                    currentPoint.symbolLocations && previousPoint.symbolLocations &&
                    currentPoint.symbolLocations[0] && previousPoint.symbolLocations[0]) {
                    const currentSegmentPoint = this.addClipRectToPosition(currentPoint.symbolLocations[0], clip);
                    const previousSegmentPoint = this.addClipRectToPosition(previousPoint.symbolLocations[0], clip);
                    if (currentSegmentPoint && previousSegmentPoint) {
                        anchorCandidates.push({
                            x: (currentSegmentPoint.x + previousSegmentPoint.x) / 2,
                            y: (currentSegmentPoint.y + previousSegmentPoint.y) / 2
                        });
                    }
                }
            }
        }
        let lastTriedInClipPosition = null;
        for (const anchor of anchorCandidates) {
            const isFilled = this.isFilledSeries(series);
            const candidatePositions = isFilled
                ? [{ x: anchor.x - textSize.width / 2, y: anchor.y + offset + 5 }]
                : [
                    { x: anchor.x - textSize.width / 2, y: anchor.y - offset - textSize.height },
                    { x: anchor.x - textSize.width / 2, y: anchor.y + offset + 5 }
                ];
            for (const position of candidatePositions) {
                if (!this.withinClip(position, textSize, clip)) {
                    continue;
                }
                lastTriedInClipPosition = { x: position.x, y: position.y };
                let rectToPush = new Rect(position.x, position.y, textSize.width, textSize.height);
                if (useShape) {
                    rectToPush = new Rect(position.x - padding, position.y - padding, textSize.width + padding * 2, textSize.height + padding * 2);
                }
                const emptyRect = { x: 0, y: 0, width: 0, height: 0 };
                if (!isCollide(rectToPush, blockers, emptyRect) && !isCollide(rectToPush, seriesRect, emptyRect)) {
                    this.chart.seriesLabelCollections.push(rectToPush);
                    blockers.push(rectToPush);
                    const color = this.getLabelColor(series, font);
                    const labelId = this.chart.element.id + '_Point_' + (series.index) + '_Text_' + (series.index);
                    // draw background shape if required
                    let shapeElement = null;
                    if (useShape) {
                        const shapeId = labelId + '_Shape';
                        const rectOption = new RectOption(shapeId, background, border, seriesLabel.opacity, rectToPush, 0, 0, '');
                        shapeElement = this.chart.renderer.drawRectangle(rectOption, new Int32Array([clip.x, clip.y]));
                        appendChildElement(this.chart.enableCanvas, element, shapeElement, this.chart.redraw, true, 'x', 'y', null, '', false, false, null, series.animation.duration);
                        if (shapeElement) {
                            if (isInitialRender) {
                                shapeElement.setAttribute('opacity', '0');
                                this.animateElementOpacity(shapeElement, 200, finalOpacity, animationDelay);
                            }
                            else {
                                // On redraw (touch/click): set final opacity immediately, no animation
                                shapeElement.setAttribute('opacity', finalOpacity.toString());
                            }
                        }
                    }
                    const labelElement = textElement(this.chart.renderer, new TextOption(labelId, position.x + textSize.width / 2, position.y + textSize.height / 1.5, 'middle', labelText), font, color, element, false, this.chart.redraw, true, false, series.animation.duration, clip, null, null, this.chart.enableCanvas, null, this.chart.themeStyle.seriesLabelFont, new ChartLocation(position.x, position.y));
                    if (labelElement) {
                        if (isInitialRender) {
                            labelElement.setAttribute('opacity', '0');
                            this.animateElementOpacity(labelElement, 200, finalOpacity, animationDelay);
                        }
                        else {
                            // On redraw (touch/click): set final opacity immediately, no animation
                            labelElement.setAttribute('opacity', finalOpacity.toString());
                        }
                        labelElements.push(labelElement);
                    }
                    return labelElements;
                }
            }
        }
        if (allowOverlap && lastTriedInClipPosition) {
            const position = lastTriedInClipPosition;
            const color = this.getLabelColor(series, font);
            const labelId = this.chart.element.id + '_Point_' + (series.index) + '_Text_' + (series.index);
            let rectToPush = new Rect(position.x, position.y, textSize.width, textSize.height);
            if (useShape) {
                rectToPush = new Rect(position.x - padding, position.y - padding, textSize.width + padding * 2, textSize.height + padding * 2);
            }
            this.chart.seriesLabelCollections.push(rectToPush);
            blockers.push(rectToPush);
            if (useShape) {
                const shapeId = labelId + '_Shape';
                const rectOption = new RectOption(shapeId, background, border, seriesLabel.opacity, rectToPush, 0, 0, '');
                const shapeElement = this.chart.renderer.drawRectangle(rectOption, new Int32Array([clip.x, clip.y]));
                appendChildElement(this.chart.enableCanvas, element, shapeElement, this.chart.redraw, true, 'x', 'y', null, '', false, false, null, series.animation.duration);
                if (shapeElement) {
                    if (isInitialRender) {
                        shapeElement.setAttribute('opacity', '0');
                        this.animateElementOpacity(shapeElement, 200, finalOpacity, animationDelay);
                    }
                    else {
                        shapeElement.setAttribute('opacity', finalOpacity.toString());
                    }
                }
            }
            const labelElement = textElement(this.chart.renderer, new TextOption(labelId, position.x + textSize.width / 2, position.y + textSize.height / 1.5, 'middle', labelText), font, color, element, false, this.chart.redraw, true, false, series.animation.duration, clip, null, null, this.chart.enableCanvas, null, this.chart.themeStyle.seriesLabelFont, new ChartLocation(position.x, position.y));
            if (labelElement) {
                if (isInitialRender) {
                    labelElement.setAttribute('opacity', '0');
                    this.animateElementOpacity(labelElement, 200, finalOpacity, animationDelay);
                }
                else {
                    labelElement.setAttribute('opacity', finalOpacity.toString());
                }
                labelElements.push(labelElement);
            }
        }
        return labelElements;
    }
    /**
     * Create rect by using two points as diagonal from the top-left and bottom-right points.
     *
     * @param {ChartLocation} startPoint - the first point's position on the chart
     * @param {ChartLocation} endPoint - the second point's position on the chart.
     * @returns {Rect} - a rect created with 2 points.
     */
    createRectFromPoints(startPoint, endPoint) {
        const x = Math.min(startPoint.x, endPoint.x);
        const y = Math.min(startPoint.y, endPoint.y);
        const width = Math.abs(endPoint.x - startPoint.x);
        const height = Math.abs(endPoint.y - startPoint.y);
        return new Rect(x, y, width, height);
    }
    /**
     * Adding clip rect to the positions of each data points before placing labels.
     *
     * @param {ChartLocation} position - Location of the point in the chart.
     * @param {Rect} clipRect - created imaginary rect per point.
     * @returns {ChartLocation} Suitable rect position for anchors.
     */
    addClipRectToPosition(position, clipRect) {
        if (!position || typeof position.x !== 'number' || typeof position.y !== 'number' ||
            !clipRect || typeof clipRect.x !== 'number' || typeof clipRect.y !== 'number') {
            return { x: 0, y: 0 };
        }
        return {
            x: position.x + clipRect.x,
            y: position.y + clipRect.y
        };
    }
    /**
     * Check the point is within the clip rectangle.
     *
     * @param {ChartLocation} pos - position of data points in chart.
     * @param {Size} size - size of label text.
     * @param {Rect} clip - created imaginary Rect for data points.
     * @returns {boolean} true if within the rect, else false.
     */
    withinClip(pos, size, clip) {
        return (pos.x >= clip.x && pos.y >= clip.y && pos.x + size.width <= clip.x + clip.width &&
            pos.y + size.height <= clip.y + clip.height);
    }
    /**
     * Render the series labels with animation.
     *
     * @param {HTMLElement} element - current label element per series.
     * @param {number} duration - animation duration.
     * @param {number} finalOpacity - opacity set by user.
     * @param {number} delay - animation delay.
     * @returns {void}
     * @private
     */
    animateElementOpacity(element, duration, finalOpacity, delay) {
        // Animate ONLY during initial series animation, not during redraw interactions.
        const isInitialSeriesAnimation = !this.chart.redraw && (this.chart).animateSeries;
        if (!isInitialSeriesAnimation || duration <= 0) {
            element.setAttribute('opacity', finalOpacity.toString());
            return;
        }
        element.setAttribute('opacity', '0');
        const startAnimation = () => {
            let startTime = null;
            const animate = (time) => {
                if (startTime === null) {
                    startTime = time;
                }
                const progress = Math.min((time - startTime) / duration, 1);
                element.setAttribute('opacity', (progress * finalOpacity).toString());
                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
                else {
                    element.setAttribute('opacity', finalOpacity.toString());
                }
            };
            requestAnimationFrame(animate);
        };
        if (delay && delay > 0) {
            setTimeout(startAnimation, delay);
        }
        else {
            startAnimation();
        }
    }
    /**
     * Determine the color for each label based on the background & theme.
     *
     * @param {Series} series - current series.
     * @param {FontModel} font - font properties.
     * @returns {string} - the color of label.
     */
    getLabelColor(series, font) {
        if (this.isFilledSeries(series)) {
            const baseColor = series.interior;
            const rgb = convertHexToColor(colorNameToHex(baseColor));
            const brightness = Math.round((rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000);
            const contrastColor = brightness >= 128 ? 'black' : 'white';
            return font.color || contrastColor;
        }
        return font.color || series.border.color || series.interior;
    }
    /**
     * Check whether the current series is a filled-type series.
     *
     * @param {Series} series - current series.
     * @returns {boolean} true if filled-type, else false.
     */
    isFilledSeries(series) {
        return (series.type === 'Area' || series.type === 'StackingArea' || series.type === 'RangeArea' ||
            series.type === 'StepArea' || series.type === 'SplineArea' || series.type === 'RangeStepArea');
    }
    /**
     * Calculate the dimensions for error bars if present.
     *
     * @param {Series} series - current series.
     * @returns {Rect[]} a rect for error bars.
     */
    buildErrorBarRects(series) {
        const rects = [];
        if (!series || !series.errorBar || !series.errorBar.visible) {
            return rects;
        }
        if (!series.chart || !series.chart.errorBarModule) {
            return rects;
        }
        const positiveHeight = series.chart.errorBarModule.positiveHeight || 0;
        const negativeHeight = series.chart.errorBarModule.negativeHeight || 0;
        // Decide which spans to include based on direction
        const direction = series.errorBar.direction;
        const includePlus = (direction === 'Plus' || direction === 'Both');
        const includeMinus = (direction === 'Minus' || direction === 'Both');
        // Cap width (fallback to a small width if not set)
        const capWidth = Math.max(6, series.errorBar.errorBarCap.width);
        for (const points of series.points) {
            if (!points || !points.visible || !points.symbolLocations || !points.symbolLocations[0]) {
                continue;
            }
            const symbolLocation = points.symbolLocations[0];
            // Base point in chart coords
            const cx = symbolLocation.x + series.clipRect.x;
            const cy = symbolLocation.y + series.clipRect.y;
            // Vertical span for blockers:
            const up = includePlus ? positiveHeight : 0;
            const down = includeMinus ? Math.abs(negativeHeight) : 0;
            if (up === 0 && down === 0) {
                continue;
            }
            const top = cy - up;
            const bottom = cy + down;
            rects.push(new Rect(cx - capWidth / 2, Math.min(top, bottom), capWidth, Math.abs(bottom - top)));
        }
        return rects;
    }
    /**
     * Get visible points based on current axis zoom state.
     *
     * @param {Series} series - The series to filter.
     * @returns {Points[]} - Array of points within the visible axis range.
     * @private
     */
    getZoomVisiblePoints(series) {
        const allVisiblePoints = getVisiblePoints(series);
        if (!series.xAxis || !series.yAxis) {
            return allVisiblePoints;
        }
        const xAxis = series.xAxis;
        const yAxis = series.yAxis;
        // Check if chart is zoomed
        const isZoomed = (xAxis.zoomFactor !== 1 || xAxis.zoomPosition !== 0 ||
            yAxis.zoomFactor !== 1 || yAxis.zoomPosition !== 0);
        if (!isZoomed) {
            return allVisiblePoints;
        }
        // Get visible ranges
        const xRange = xAxis.visibleRange;
        const yRange = yAxis.visibleRange;
        // Filter points within visible range
        return allVisiblePoints.filter((point) => {
            if (!point || point.x === null || point.y === null) {
                return false;
            }
            const xValue = typeof point.x === 'number' ? point.x :
                point.x instanceof Date ? point.x.getTime() :
                    parseFloat(point.x);
            if (xValue < xRange.min || xValue > xRange.max) {
                return false;
            }
            const yValue = typeof point.y === 'number' ? point.y : parseFloat(point.y);
            if (yValue < yRange.min || yValue > yRange.max) {
                return false;
            }
            return true;
        });
    }
    /**
     * Clear existing series labels from the chart.
     *
     * @returns {void}
     * @private
     */
    clearLabels() {
        if (this.chart.seriesLabelElements) {
            while (this.chart.seriesLabelElements.firstChild) {
                this.chart.seriesLabelElements.removeChild(this.chart.seriesLabelElements.firstChild);
            }
        }
        // Clear the collision collection
        this.chart.seriesLabelCollections = [];
    }
    /**
     * Get module name.
     *
     * @returns {string} - Returns the module name.
     */
    getModuleName() {
        // Returns the module name
        return 'SeriesLabel';
    }
    /**
     * To destroy the series label module.
     *
     * @returns {void}
     * @private
     */
    destroy() {
        // Destroy method performed here
    }
}
